import { FC, useCallback, useMemo } from 'react';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import useConfirm from 'src/hooks/useConfirm';
import usePeriodView from 'src/hooks/usePeriodView';
import useViewCheckbook from 'src/hooks/useViewCheckbook';

interface Props {}

const BulkManageAttributes: FC<Props> = () => {
  const { hasManageGLCodeAttributesPermission, isPeriodTableDirty } = useViewCheckbook();
  const {
    glCodeAtrPanelOpen,
    setSelectedGLCode,
    selectedRows,
    handleBulkGLCodeAttribute,
    setInitialSelectedGLCode,
  } = usePeriodView();
  const { confirm } = useConfirm();

  const isBulkAttributesEnabled = useMemo(
    () =>
      selectedRows.length &&
      selectedRows.every((row) => !row.isNewGlCode) &&
      selectedRows.every((row) => row.isActive),
    [selectedRows]
  );

  const hasUnsaveChanges = useMemo(
    () => selectedRows.length && selectedRows.some((row) => row.isNewGlCode),
    [selectedRows]
  );

  const handleClick = useCallback(() => {
    glCodeAtrPanelOpen();
    setSelectedGLCode(selectedRows);
    handleBulkGLCodeAttribute();
    setInitialSelectedGLCode(selectedRows);
  }, [
    glCodeAtrPanelOpen,
    handleBulkGLCodeAttribute,
    selectedRows,
    setInitialSelectedGLCode,
    setSelectedGLCode,
  ]);

  const handleManageGLCodeAttributeClick = useCallback(
    (e: any) => {
      if (isPeriodTableDirty) {
        confirm({
          title: 'Unsaved Changes',
          content:
            'You have unsaved changes. Please go back (cancel) to first save them. Otherwise, you can proceed and your changes will be lost.',
          cancelButtonText: 'Cancel',
          confirmButtonText: 'Discard and Proceed',
          width: '725px',
        }).then((isAccepted) => {
          if (isAccepted) {
            handleClick();
          }
        });
      } else {
        handleClick();
      }
    },
    [isPeriodTableDirty, confirm, handleClick]
  );

  const data = () => {
    const infos: string[] = [];
    const errors: string[] = [];
    if (hasUnsaveChanges) infos.push('Save changes to manage attributes');
    if (selectedRows.length === 0) infos.push('Select GL Code(s) to manage attributes');
    if (selectedRows.length > 0 && !selectedRows.every((row) => row.isActive))
      errors.push('Please select Active and Undeleted GL Codes');

    return { infos, errors };
  };

  const { infos, errors } = data();

  return hasManageGLCodeAttributesPermission ? (
    <PopUp
      popupAlign={{ horizontal: 'center', vertical: 'top' }}
      content={
        <div className="m-1 max-w-[350px]">
          <PopUpContentWrapper type="Information" contents={infos} />
          <PopUpContentWrapper type="Error" contents={errors} />
        </div>
      }
      show={!isBulkAttributesEnabled}
    >
      <Button
        color="info"
        disabled={!isBulkAttributesEnabled}
        onClick={handleManageGLCodeAttributeClick}
      >
        Bulk Manage GL Code Attributes
      </Button>
    </PopUp>
  ) : (
    <></>
  );
};

export default BulkManageAttributes;
